import React, { createContext, useContext, useState, useCallback } from 'react';
import { AuthContextType, User } from '../types/auth';
import api from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const storedUser = localStorage.getItem('@App:user');
      if (!storedUser) return null;
      
      const parsedUser = JSON.parse(storedUser);
      if (!parsedUser || typeof parsedUser !== 'object') return null;
      
      // Valida se o objeto tem as propriedades necessárias
      if (!('id' in parsedUser) || !('email' in parsedUser) || !('name' in parsedUser) || !('role' in parsedUser)) {
        console.error('Dados do usuário inválidos no localStorage');
        localStorage.removeItem('@App:user');
        return null;
      }
      
      return parsedUser;
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error);
      localStorage.removeItem('@App:user');
      return null;
    }
  });

  const [token, setToken] = useState<string | null>(() => {
    try {
      const storedToken = localStorage.getItem('@App:token');
      return storedToken || null;
    } catch (error) {
      console.error('Erro ao carregar token:', error);
      localStorage.removeItem('@App:token');
      return null;
    }
  });

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await api.post('/api/auth/login', { email, password });
      
      // Se a resposta vier como string, faz o parse
      let data;
      if (typeof response.data === 'string') {
        // Substitui "Infinity" por um número muito grande antes do parse
        const jsonString = response.data.replace(/:Infinity/g, ':1e308');
        data = JSON.parse(jsonString);
      } else {
        data = response.data;
      }
      
      const { access_token, token_type, user } = data;
      
      if (!access_token || !user) {
        throw new Error('Dados de autenticação inválidos');
      }

      // Primeiro configura o token para as próximas requisições
      api.defaults.headers.common['Authorization'] = `${token_type} ${access_token}`;

      // Depois salva os dados
      localStorage.setItem('@App:token', access_token);
      localStorage.setItem('@App:user', JSON.stringify(user));
      
      // Por último, atualiza o estado
      setUser(user);
      setToken(access_token);
    } catch (error: any) {
      // Limpa qualquer dado antigo que possa existir
      localStorage.removeItem('@App:token');
      localStorage.removeItem('@App:user');
      setUser(null);
      setToken(null);
      delete api.defaults.headers.common['Authorization'];

      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('@App:token');
    localStorage.removeItem('@App:user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
  }, []);

  const updateUser = useCallback((updatedUser: User) => {
    if (!updatedUser) {
      console.error('Tentativa de atualizar usuário com valor inválido:', updatedUser);
      return;
    }
    localStorage.setItem('@App:user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  }, []);

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        token,
        isAuthenticated: !!token,
        login, 
        logout,
        updateUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 